import { MenuItem, Select, Stack, Typography } from '@mui/material'
import { useMemo, useState } from 'react'

import { PLGHandler } from './shadCN/PLGHandler'

import { type CardActivityWithId, GRADING_METHOD_OPTIONS } from '@/models/card-activity/card-activity.model'
import { withPermissionCheck, type WithPermissionProps } from '@/hoc/WithPermissionCheck'
import { ReactComponent as ChevronDownIcon } from '@/assets/icons/chevron-down.svg'

export interface GradingMethodSelectOptionProps {
  option: {
    title: ReactNode
    value: CardActivityWithId['grading_method']
    bg: string
    icon: React.ElementType
    detailedTitle: string
    description: string
  }
  onSelect: (value: CardActivityWithId['grading_method']) => void
  handleCloseSelect: () => void
}

export function GradingMethodSelectOption({
  option,
  disabled = false,
  handleOpenPopover,
  onSelect,
  handleCloseSelect,
}: WithPermissionProps<GradingMethodSelectOptionProps>) {
  const Icon = option.icon

  return (
    <Stack
      alignItems="center"
      direction="row"
      gap="4px"
      justifyContent="space-between"
      width="100%"
      onClick={e => {
        if (disabled) {
          handleOpenPopover?.(e)
          e.preventDefault()
          e.stopPropagation()

          return
        }
        onSelect(option.value)
        handleCloseSelect()
      }}
    >
      <Stack alignItems="center" direction="row" gap={1} width="100%">
        <Stack
          bgcolor={option.bg}
          borderRadius="12px"
          height="fit-content"
          padding="8px"
          position="relative"
          width="max-content"
        >
          <Icon color="#fff" height={16} width={16} />
        </Stack>
        <Typography>
          {option.detailedTitle}: {option.description}
        </Typography>
      </Stack>
      {/* <PLGHandler
        visible={disabled ?? false}
        onMouseEnter={e => {
          handleOpenPopover?.(e)
        }}
      /> */}
    </Stack>
  )
}

export function GradingMethodSelect({
  gradingMethod,
  onChange,
  icon,
  maxWidth = '290px',
}: {
  gradingMethod: string
  onChange: (value: CardActivityWithId['grading_method']) => void
  icon?: JSX.Element
  maxWidth?: string
}) {
  const [open, setOpen] = useState(false)

  const handleClose = () => {
    setOpen(false)
  }

  const handleOpen = () => {
    setOpen(true)
  }

  return (
    <Select
      IconComponent={ChevronDownIcon}
      open={open}
      renderValue={value => {
        const selectedOption = GRADING_METHOD_OPTIONS.find(option => option.value === value)

        if (!selectedOption) return ''

        return (
          <Stack alignItems="center" direction="row" gap={1}>
            {icon}
            {selectedOption.icon ? (
              <IconGradingMethod bgcolor={selectedOption.bg} icon={<selectedOption.icon />} />
            ) : null}
            <Typography
              fontWeight="medium"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {selectedOption.detailedTitle}: {selectedOption.description}
            </Typography>
          </Stack>
        )
      }}
      size="small"
      sx={{
        borderRadius: '16px',
        '& .MuiOutlinedInput-notchedOutline': {
          border: '1px solid #D0D5DD !important',
          borderRadius: '16px !important',
        },
        '& .MuiInputBase-input': {
          padding: '14px',
          color: '#344054',
          fontWeight: 500,
        },
        '&.MuiInputBase-root': {
          height: 44,
          background: '#FFF',
          maxWidth: maxWidth,
          width: '100%',
        },
      }}
      value={gradingMethod}
      onClose={handleClose}
      onOpen={handleOpen}
    >
      {GRADING_METHOD_OPTIONS.map(option => (
        <MenuItem key={option.value} value={option.value}>
          <GradingMethodSelectOptionWithPermission
            key={option.value}
            handleCloseSelect={handleClose}
            option={option}
            requiredPermission={option.permission}
            onSelect={onChange}
          />
        </MenuItem>
      ))}
    </Select>
  )
}

function GradingMethodSelectOptionWithPermission({
  requiredPermission,
  ...props
}: {
  requiredPermission: string | string[]
} & WithPermissionProps<GradingMethodSelectOptionProps>) {
  const Component = useMemo(
    () => withPermissionCheck(GradingMethodSelectOption, requiredPermission),
    [requiredPermission],
  )

  return <Component {...props} />
}

function GradingMethodSelectCardOptionWithPermission({
  requiredPermission,
  ...props
}: {
  requiredPermission: string | string[]
} & WithPermissionProps<GradingMethodSelectCardOptionProps>) {
  const Component = useMemo(
    () => withPermissionCheck(GradingMethodSelectCardOption, requiredPermission),
    [requiredPermission],
  )

  return <Component {...props} />
}

interface GradingMethodSelectCardOptionProps {
  option: GradingMethodSelectOptionProps['option']
  isSelected: boolean
  onChange: (value: CardActivityWithId['grading_method']) => void
}

function GradingMethodSelectCardOption({
  option,
  isSelected,
  onChange,
  disabled,
  handleOpenPopover,
}: WithPermissionProps<GradingMethodSelectCardOptionProps>) {
  return (
    <Stack
      key={option.value}
      sx={{
        height: '100%',
        minWidth: '140px',
        width: '100%',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        gap: '10px',
        flexDirection: 'row',
        padding: '8px',
        borderRadius: '16px',
        border: isSelected ? '1px solid #EE46BC' : '1px solid #EAECF0',
        bgcolor: isSelected ? '#FDF2FA' : '#fff',
        ...(!disabled && {
          '&:hover': {
            border: '1px solid #EE46BC',
            bgcolor: '#FDF2FA',
          },
        }),
      }}
      onClick={e => {
        if (disabled) {
          handleOpenPopover?.(e)

          return
        }

        onChange(option.value)
      }}
    >
      <IconGradingMethod bgcolor={option.bg} icon={<option.icon />} />
      <Stack>
        <Typography fontWeight="medium">{option.title}</Typography>
        <Typography color="#344054" fontSize={8}>
          {option.description}
        </Typography>
      </Stack>
      {/* <PLGHandler
        visible={disabled ?? false}
        onMouseEnter={e => {
          handleOpenPopover?.(e)
        }}
      /> */}
    </Stack>
  )
}

export function GradingMethodSelectCards({
  value,
  onChange,
}: {
  value: CardActivityWithId['grading_method']
  onChange: (value: CardActivityWithId['grading_method']) => void
}) {
  return (
    <Stack sx={{ gap: '6px', flexDirection: 'row' }}>
      {GRADING_METHOD_OPTIONS.map(option => {
        const isSelected = option.value === value

        return (
          <GradingMethodSelectCardOptionWithPermission
            key={option.value}
            isSelected={isSelected}
            option={option}
            requiredPermission={[]} //option.permission}
            onChange={onChange}
          />
        )
      })}
    </Stack>
  )
}

function IconGradingMethod({ bgcolor, icon }: { bgcolor: string; icon: JSX.Element }) {
  return (
    <Stack
      bgcolor={bgcolor}
      borderRadius="12px"
      height="fit-content"
      padding="8px"
      position="relative"
      width="max-content"
    >
      {icon}
    </Stack>
  )
}
